import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Terminal } from 'lucide-react';

const Loading = () => {
  const [text, setText] = useState('');
  const [currentLine, setCurrentLine] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  // Programming-themed loading text
  const loadingLines = [
    'Initializing system...',
    'npm install dependencies...',
    'Compiling components...',
    'Optimizing render functions...',
    'Fetching data from API...',
    'Parsing JSON response...',
    'Building UI components...',
    'Applying animations...',
    'Starting development server...',
    'Launching application...'
  ];

  // Typing effect for the current line
  useEffect(() => {
    const line = loadingLines[currentLine];
    if (text.length < line.length) {
      const timeout = setTimeout(() => {
        setText(text + line[text.length]);
      }, 50); // Typing speed
      return () => clearTimeout(timeout);
    } else {
      // Move to next line after a delay
      const timeout = setTimeout(() => {
        setText('');
        setCurrentLine((prev) => (prev + 1) % loadingLines.length);
      }, 1000); // Delay before next line
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, currentLine]);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Progress calculation
  const progress = ((currentLine * 100) / loadingLines.length) +
    ((text.length / loadingLines[currentLine].length) * (100 / loadingLines.length));

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center p-6 font-sans relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-[40vw] h-[40vw] bg-red-600/5 blur-[120px] rounded-full animate-pulse" />
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl relative z-10"
      >
        {/* Editor Window */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
          {/* Editor Header */}
          <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-xs font-mono font-medium">
              <Terminal size={14} /> portfolio.jsx
            </div>
            <div className="w-16" /> {/* Spacer for balance */}
          </div>

          {/* Editor Body */}
          <div className="p-6 font-mono text-sm leading-relaxed flex">
            {/* Line Numbers */}
            <div className="pr-4 text-right text-slate-600 select-none flex flex-col items-end">
              {Array.from({ length: 11 }, (_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>

            {/* Code Content */}
            <div className="flex-1 overflow-x-auto text-slate-300">
              <div className="whitespace-nowrap"><span className="text-red-500 font-medium">import</span> React <span className="text-red-500 font-medium">from</span> <span className="text-red-400">'react'</span>;</div>
              <div className="whitespace-nowrap"><span className="text-red-500 font-medium">import</span> Portfolio <span className="text-red-500 font-medium">from</span> <span className="text-red-400">'./Portfolio'</span>;</div>
              <div>&nbsp;</div>
              <div className="whitespace-nowrap"><span className="text-red-500 font-medium">const</span> <span className="text-blue-400">App</span> = () =&gt; {'{'}</div>
              
              {/* Dynamic Loading Line */}
              <div className="whitespace-nowrap pl-4">
                <span className="text-slate-500 italic">{`// ${loadingLines[currentLine]}`}</span>
              </div>
              <div className="whitespace-nowrap pl-4 flex items-center h-6">
                <span className="text-green-400 font-bold mr-2">$</span>
                <span className="text-white">{text}</span>
                <span className={`w-2 h-4 bg-slate-400 ml-1 inline-block ${showCursor ? 'opacity-100' : 'opacity-0'}`} />
              </div>
              
              <div className="whitespace-nowrap pl-4 mt-2"><span className="text-red-500 font-medium">return</span> {'('}</div>
              <div className="whitespace-nowrap pl-8">&lt;<span className="text-yellow-400">Portfolio</span> /&gt;</div>
              <div className="whitespace-nowrap pl-4">{')'};</div>
              <div className="whitespace-nowrap">{'}'};</div>
              <div>&nbsp;</div>
              <div className="whitespace-nowrap"><span className="text-red-500 font-medium">export default</span> App;</div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">System Boot</span>
            <span className="text-xs font-bold text-red-500 font-mono">{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 w-full bg-slate-900 rounded-full overflow-hidden border border-slate-800">
            <div 
              className="h-full bg-red-600 transition-all duration-300 ease-out relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/20 w-full animate-[shimmer_2s_infinite]" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Loading;
